import React from "react";
import { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";
import { Link } from "react-router-dom";

import { DateTime } from "luxon";

import DelayInput from "../DelayInput";

import { V2 } from "../../Api";
import { formatDateLocalMonth, formatDateLocalNumeric, formatDateTimeLocalNumeric, formatName, truncateEllipse } from "../../Utilities";
import { IAuthProps/*, WellKnownGroups*/ } from "../AuthContext";
import ProgressBar from "../ProgressBar";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../blocks.css";
import "../../table.css";
import "../viewer.css";

interface ISurgeryYourCasesViewState {
    isLoading: boolean | string;
    isMarketer: boolean;

    appointments: any[];
    bonuses: any[];
    locations: any[];
    stats: any;

    searchLocation: string;
    searchOffset: number;
    searchStatus: string;
    searchType: string;

    searchStart: string;

    [name: string]: any;
}

interface ISurgeryYourCasesViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps {
}

const PAGE_SIZE = 50;

class SurgeryYourCasesView extends React.Component<ISurgeryYourCasesViewProps, ISurgeryYourCasesViewState> {
    constructor(props: Readonly<ISurgeryYourCasesViewProps>) {
        super(props);

        const state: ISurgeryYourCasesViewState = {
            isLoading: true,
            isMarketer: false, /*props.authContext.isInGroup(WellKnownGroups.Marketer),*/

            appointments: [],
            bonuses: [],
            locations: [],
            stats: { count: 0 },

            searchLocation: props.location.state ? props.location.state.searchLocation : "",
            searchOffset: (props.location.state ? props.location.state.searchOffset : 0) || 0,
            searchStatus: props.location.state ? props.location.state.searchStatus : "",
            searchType: props.location.state ? props.location.state.searchType : "",

            searchStart: props.location.state ? props.location.state.searchStart : new Date().toLocalISOString().substring(0, 10),
        };

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
    }

    public async componentDidMount() {
        try {
            const [locations, bonuses] = await Promise.all([
                V2.get<any[]>("locations?type=surgery"),
                V2.get<any[]>("bonus?group=0"),
            ]);

            this.setState({
                bonuses,
                locations
            }, () => this.search());
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        let table;

        if (this.state.isLoading === true) {
            table = <div className="spinner-center lds-dual-ring" />;
        }
        else if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else {
            let tbody;

            if (this.state.appointments.length === 0) {
                tbody = (
                    <tr>
                        <td colSpan={10}>No appointments found</td>
                    </tr>
                );
            }
            else {
                tbody = this.state.appointments.map(a => (
                    <tr key={a.id} className={this.statusToClass(a.status)}>
                        <td data-caption="Scheduled For: ">{a.time ? formatDateTimeLocalNumeric(`${a.date.substring(0, 10)}T${a.time}`) : formatDateLocalNumeric(Date.fromLocalDate(a.date.substring(0, 10)))}</td>
                        <td data-caption="Name: " className="wrap">{formatName(a.patient.name)}</td>
                        <td data-caption="Type: ">{a.primarySurgery.shortName}</td>
                        <td data-caption="Provider: " className="wrap">{formatName(a.provider.name)}</td>
                        {
                            this.state.isMarketer
                                ? <td data-caption="Type: ">{(a.bonus && a.bonus.name) || "Unset"}</td>
                                : <td data-caption="Scheduled Date: ">{formatDateTimeLocalNumeric(a.creationTimestamp)}</td>
                        }
                        <td data-caption="Note: " className="wrap">{truncateEllipse(a.note, 40)}</td>
                        <td>
                            <Link className="button small" to={`/surgery/${a.id}`}>View</Link>
                        </td>
                    </tr>
                ));
            }

            table = (
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Scheduled For</th>
                            <th>Name</th>
                            <th>Surgery</th>
                            <th>Provider</th>
                            {
                                this.state.isMarketer
                                    ? <th>Type</th>
                                    : <th>Created On</th>
                            }
                            <th>Note</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Surgery Appointments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tbody}
                    </tbody>
                </table>
            );
        }

        return (
            <div className="viewer-root">
                <div className="filter-control-box">
                    <div className="control-group">
                        <span>Start Date:</span>
                        <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <span className="fill-space" />
                    {
                        this.state.isMarketer
                            ? <select value={this.state.searchType} name="searchType" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                                <option value="">All Types</option>
                                <option value="0">Unset</option>
                                {this.state.bonuses.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                            : ""
                    }
                    <select value={this.state.searchStatus} name="searchStatus" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="complete">Completed</option>
                        <option value="noshow">Soft-canceled</option>
                        <option value="canceled">Canceled</option>
                        <option value="notneeded">Canceled - Not Needed</option>
                    </select>
                    <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Locations</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                </div>

                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>{this.state.searchStart ? formatDateLocalMonth(`${this.state.searchStart}T00:00:00`, true) : "All"} Stats by Status</h3>
                        </header>
                        <dl className="info-list">
                            <dt>{this.state.searchStatus || this.state.searchType ? "Filtered " : ""}Total Cases</dt>
                            <dd>{this.state.stats && this.state.stats.count}</dd>
                        </dl>
                        <div className="stat-list">
                            {
                                this.state.isLoading === true
                                    ? <div className="spinner spinner-center lds-dual-ring" />
                                    : this.state.stats.status
                                        ? this.state.stats.status.map((a: any) =>
                                            <div key={a.status} className="stat-row">
                                                <div className="stat-text">
                                                    <span>{this.statusToText(a.status)}</span>
                                                    <span>{a.count} ({((a.count / this.state.stats.count) * 100).toFixed(0)}%)</span>
                                                </div>
                                                <ProgressBar value={(a.count / this.state.stats.count) * 100} />
                                            </div>
                                        )
                                        : ""
                            }
                        </div>
                    </section>
                    {
                        this.state.isMarketer
                            ? <section className="info-block">
                                <header>
                                    <h3>{this.state.searchStart ? formatDateLocalMonth(`${this.state.searchStart}T00:00:00`, true) : "All"} Stats by Type</h3>
                                </header>
                                <dl className="info-list">
                                    <dt>{this.state.searchStatus || this.state.searchType ? "Filtered " : ""}Total Cases</dt>
                                    <dd>{this.state.stats && this.state.stats.count}</dd>
                                </dl>
                                <div className="stat-list">
                                    {
                                        this.state.isLoading === true
                                            ? <div className="spinner spinner-center lds-dual-ring" />
                                            : this.state.stats.bonus
                                                ? this.state.stats.bonus.map((a: any) =>
                                                    <div key={a.id} className="stat-row">
                                                        <div className="stat-text">
                                                            <span>{a.name || "Unset"}</span>
                                                            <span>{a.count} ({((a.count / this.state.stats.count) * 100).toFixed(0)}%)</span>
                                                        </div>
                                                        <ProgressBar value={(a.count / this.state.stats.count) * 100} />
                                                    </div>
                                                )
                                                : ""
                                    }
                                </div>
                            </section>
                            : <section className="spacer" />
                    }
                </div>
                &nbsp;
                {table}
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.appointments.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        }, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchLocation: this.state.searchLocation,
            searchOffset: this.state.searchOffset,
            searchPerson: this.state.searchPerson,
            searchStatus: this.state.searchStatus,
            searchText: this.state.searchText,
            searchType: this.state.searchType,

            // searchEnd: this.state.searchEnd,
            searchStart: this.state.searchStart,
        };

        this.props.history.replace(location);
    }

    private async search() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters: string[] = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`,
                `person=${this.props.authContext.user.employee.id}`,
                "mode=verbose",
            ];
            const statsParameters: string[] = [
                `person=${this.props.authContext.user.employee.id}`,
                this.state.isMarketer ? "mode=status_bonus" : "mode=status"
            ];
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
                statsParameters.push(`location=${this.state.searchLocation}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
                statsParameters.push(`status=${this.state.searchStatus}`);
            }
            if (this.state.searchStart) {
                parameters.push(`after=${this.state.searchStart}`);

                const startDate = DateTime.fromISO(this.state.searchStart).startOf("month");
                const endDate = startDate.plus({ month: 1 });

                statsParameters.push(`after=${startDate.toISODate()}`);
                statsParameters.push(`before=${endDate.toISODate()}`);
            }
            if (this.state.searchType) {
                parameters.push(`m_bonus_type=${this.state.searchType}`);
                statsParameters.push(`m_bonus_type=${this.state.searchType}`);
            }

            const [results, stats] = await Promise.all([
                V2.get<any[]>(`surgery/summary?${parameters.join("&")}`),
                V2.get<any[]>(`surgery/stats?${statsParameters.join("&")}`)
            ]);

            for (const item of results) {
                item.note = (item.note || "").replace(/[\r\n]+/g, " ");
            }

            this.setState({
                appointments: results,
                isLoading: false,
                stats
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Failed to search"
            });
        }
    }

    private statusToClass(status: number) {
        switch (status) {
            case 4: return "row-green";
            case 3: return "row-yellow";
            case 2: return "row-red";
            case 1:
            case 0:
            default: return "";
        }
    }

    private statusToText(status: number) {
        switch (status) {
            case 0: return "Pending";
            case 1: return "Active";
            case 2: return "Canceled";
            case 3: return "Soft-canceled";
            case 4: return "Complete";
            default: return `Unknown: ${status}`;
        }
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.search());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.search());
    }
}

export default withTitle("Your Cases", withAuth(SurgeryYourCasesView));